// Static interface copy in every supported app language. Data (agent names, connection names,
// action titles and quoted arguments) is not translated here.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  kindAgentRequest: 'Agent request',
  kindAgentActivity: 'Agent activity',
  waitingForDecision: 'Waiting for a decision',
  fromAgent: 'From {agent}, {when}',
  fromAgentOnly: 'From {agent}',
  unnamedAgent: 'An AI agent',
  describe: '{agent} asks to do this for you. Nothing happens until you approve.',
  where: 'Where:',
  whereAsYou: '{where}, as you.',
  connectionWrite: 'The connection is set to Read & write.',
  whyAsks: 'Why it asks:',
  whyAskConnections:
    '{agent}’s connections are set to Ask each time, and this action isn’t on its allowed list yet.',
  whyAsk: '{agent} is set to Ask each time here, and this action isn’t on its allowed list yet.',
  whyFloor: 'This kind of action always asks you, whatever {agent} is set to.',
  whyEmail: 'Gmail and Outlook sends always ask you, whatever {agent} is set to.',
  whyUnknown: 'Poneva can’t tell what this changes, so it asks you first.',
  seeWhatItMayDo: 'See what it may do',
  othersLikeThis: 'Others like this:',
  allowAgain: 'Don’t ask again for {action}',
  allowAgainDetail: '{agent} can then do this without asking. You can undo this in Connections.',
  approve: 'Approve',
  decline: 'Decline',
  approved: 'Approved. {agent} can go on.',
  declined: 'Declined. Nothing ran.',
  requestVoid: 'This request is no longer waiting, so nothing ran.',
  allowAgainUnavailable:
    'Poneva can’t remember this choice for this action. Approve again to allow it just this once.',
  decisionFailed: 'We couldn’t send your decision. Try again.',
  loadFailed: 'We couldn’t load this request. Try again in a moment.',
  loading: 'Loading…',
  alreadyDecided: 'This request has already been decided.',
  familyLibraryRead: 'Read your Poneva work',
  familyLibraryWrite: 'Save work and ask Poneva',
  familyAppsAuthor: 'Build and change Apps',
  familyDataConnections: 'Use your connections',
  familyOther: 'Poneva actions',
  allowedWithoutAsking: 'Allowed without asking',
  ranAutomatically: 'Ran automatically',
  refusedRead: '{name} is set to Read, so nothing changed',
  refusedNothing: 'Nothing changed',
};

export type AgentRequestsCopyKey = keyof typeof en;
export type AgentRequestsCopy = Readonly<Record<AgentRequestsCopyKey, string>>;

const agentRequestsCopy: NamespaceCatalog<AgentRequestsCopyKey> = {
  bg: {
    kindAgentRequest: 'Заявка от агент',
    kindAgentActivity: 'Дейност на агент',
    waitingForDecision: 'Чака решение',
    fromAgent: 'От {agent}, {when}',
    fromAgentOnly: 'От {agent}',
    unnamedAgent: 'ИИ агент',
    describe: '{agent} иска да направи това вместо вас. Нищо не се случва, докато не одобрите.',
    where: 'Къде:',
    whereAsYou: '{where}, от ваше име.',
    connectionWrite: 'Връзката е настроена на „Четене и писане“.',
    whyAsks: 'Защо пита:',
    whyAskConnections:
      'Връзките на {agent} са настроени на „Питай всеки път“ и това действие още не е в списъка на разрешените.',
    whyAsk:
      '{agent} е настроен на „Питай всеки път“ тук и това действие още не е в списъка на разрешените.',
    whyFloor: 'Този вид действие винаги пита вас, независимо от настройката на {agent}.',
    whyEmail:
      'Изпращанията от Gmail и Outlook винаги питат вас, независимо от настройката на {agent}.',
    whyUnknown: 'Poneva не може да определи какво променя това, затова първо пита вас.',
    seeWhatItMayDo: 'Вижте какво може да прави',
    othersLikeThis: 'Други като това:',
    allowAgain: 'Не питай отново за {action}',
    allowAgainDetail:
      '{agent} ще може да прави това без да пита. Можете да го отмените в „Връзки“.',
    approve: 'Одобри',
    decline: 'Откажи',
    approved: 'Одобрено. {agent} може да продължи.',
    declined: 'Отказано. Нищо не е изпълнено.',
    requestVoid: 'Тази заявка вече не чака, затова нищо не е изпълнено.',
    allowAgainUnavailable:
      'Poneva не може да запомни този избор за това действие. Одобрете отново, за да го разрешите само този път.',
    decisionFailed: 'Не можахме да изпратим решението ви. Опитайте отново.',
    loadFailed: 'Не можахме да заредим тази заявка. Опитайте отново след малко.',
    loading: 'Зареждане…',
    alreadyDecided: 'За тази заявка вече е взето решение.',
    familyLibraryRead: 'Четене на вашата работа в Poneva',
    familyLibraryWrite: 'Запазване на работа и въпроси към Poneva',
    familyAppsAuthor: 'Създаване и промяна на приложения',
    familyDataConnections: 'Използване на вашите връзки',
    familyOther: 'Действия в Poneva',
    allowedWithoutAsking: 'Разрешено без питане',
    ranAutomatically: 'Изпълнено автоматично',
    refusedRead: '{name} е настроена на „Четене“, затова нищо не се промени',
    refusedNothing: 'Нищо не се промени',
  },
  hr: {
    kindAgentRequest: 'Zahtjev agenta',
    kindAgentActivity: 'Aktivnost agenta',
    waitingForDecision: 'Čeka odluku',
    fromAgent: 'Od: {agent}, {when}',
    fromAgentOnly: 'Od: {agent}',
    unnamedAgent: 'AI agent',
    describe: '{agent} traži da to učini umjesto vas. Ništa se ne događa dok ne odobrite.',
    where: 'Gdje:',
    whereAsYou: '{where}, kao vi.',
    connectionWrite: 'Veza je postavljena na Čitanje i pisanje.',
    whyAsks: 'Zašto pita:',
    whyAskConnections:
      'Veze agenta {agent} postavljene su na Pitaj svaki put, a ova radnja još nije na popisu dopuštenih.',
    whyAsk:
      'Agent {agent} je ovdje postavljen na Pitaj svaki put, a ova radnja još nije na popisu dopuštenih.',
    whyFloor: 'Ova vrsta radnje uvijek pita vas, bez obzira na postavku agenta {agent}.',
    whyEmail:
      'Slanja iz Gmaila i Outlooka uvijek pitaju vas, bez obzira na postavku agenta {agent}.',
    whyUnknown: 'Poneva ne može procijeniti što ovo mijenja pa prvo pita vas.',
    seeWhatItMayDo: 'Pogledajte što smije raditi',
    othersLikeThis: 'Slično ovome:',
    allowAgain: 'Ne pitaj ponovno za {action}',
    allowAgainDetail: '{agent} to tada može činiti bez pitanja. To možete poništiti u Vezama.',
    approve: 'Odobri',
    decline: 'Odbij',
    approved: 'Odobreno. {agent} može nastaviti.',
    declined: 'Odbijeno. Ništa nije pokrenuto.',
    requestVoid: 'Ovaj zahtjev više ne čeka pa ništa nije pokrenuto.',
    allowAgainUnavailable:
      'Poneva ne može zapamtiti ovaj izbor za ovu radnju. Odobrite ponovno kako biste je dopustili samo ovaj put.',
    decisionFailed: 'Nismo mogli poslati vašu odluku. Pokušajte ponovno.',
    loadFailed: 'Nismo mogli učitati ovaj zahtjev. Pokušajte ponovno za trenutak.',
    loading: 'Učitavanje…',
    alreadyDecided: 'O ovom zahtjevu već je odlučeno.',
    familyLibraryRead: 'Čitanje vašeg rada u Poneva',
    familyLibraryWrite: 'Spremanje rada i pitanja za Ponevu',
    familyAppsAuthor: 'Izrada i promjena aplikacija',
    familyDataConnections: 'Korištenje vaših veza',
    familyOther: 'Radnje u Poneva',
    allowedWithoutAsking: 'Dopušteno bez pitanja',
    ranAutomatically: 'Pokrenuto automatski',
    refusedRead: 'Veza {name} postavljena je na Čitanje pa se ništa nije promijenilo',
    refusedNothing: 'Ništa se nije promijenilo',
  },
  cs: {
    kindAgentRequest: 'Žádost agenta',
    kindAgentActivity: 'Aktivita agenta',
    waitingForDecision: 'Čeká na rozhodnutí',
    fromAgent: 'Od: {agent}, {when}',
    fromAgentOnly: 'Od: {agent}',
    unnamedAgent: 'AI agent',
    describe: '{agent} žádá o provedení této akce za vás. Nic se nestane, dokud ji neschválíte.',
    where: 'Kde:',
    whereAsYou: '{where}, jako vy.',
    connectionWrite: 'Připojení je nastaveno na Čtení a zápis.',
    whyAsks: 'Proč se ptá:',
    whyAskConnections:
      'Připojení agenta {agent} jsou nastavena na Ptát se pokaždé a tato akce zatím není na seznamu povolených.',
    whyAsk:
      'Agent {agent} je tu nastaven na Ptát se pokaždé a tato akce zatím není na seznamu povolených.',
    whyFloor: 'Tento druh akce se vás ptá vždy, ať je {agent} nastaven jakkoli.',
    whyEmail: 'Odesílání z Gmailu a Outlooku se vás ptá vždy, ať je {agent} nastaven jakkoli.',
    whyUnknown: 'Poneva nedokáže určit, co se tím změní, proto se nejdřív zeptá vás.',
    seeWhatItMayDo: 'Podívejte se, co smí dělat',
    othersLikeThis: 'Další podobné:',
    allowAgain: 'Neptat se znovu na {action}',
    allowAgainDetail: '{agent} to pak může dělat bez ptaní. Můžete to vrátit v Připojeních.',
    approve: 'Schválit',
    decline: 'Odmítnout',
    approved: 'Schváleno. {agent} může pokračovat.',
    declined: 'Odmítnuto. Nic neproběhlo.',
    requestVoid: 'Tato žádost už nečeká, takže nic neproběhlo.',
    allowAgainUnavailable:
      'Poneva si tuto volbu pro tuto akci nemůže zapamatovat. Schvalte ji znovu, aby se povolila jen tentokrát.',
    decisionFailed: 'Rozhodnutí se nepodařilo odeslat. Zkuste to znovu.',
    loadFailed: 'Tuto žádost se nepodařilo načíst. Zkuste to za chvíli znovu.',
    loading: 'Načítání…',
    alreadyDecided: 'O této žádosti už bylo rozhodnuto.',
    familyLibraryRead: 'Čtení vaší práce v Poneva',
    familyLibraryWrite: 'Ukládání práce a dotazy na Ponevu',
    familyAppsAuthor: 'Vytváření a úpravy aplikací',
    familyDataConnections: 'Používání vašich připojení',
    familyOther: 'Akce v Poneva',
    allowedWithoutAsking: 'Povoleno bez ptaní',
    ranAutomatically: 'Provedeno automaticky',
    refusedRead: 'Připojení {name} je nastaveno na Čtení, takže se nic nezměnilo',
    refusedNothing: 'Nic se nezměnilo',
  },
  da: {
    kindAgentRequest: 'Anmodning fra agent',
    kindAgentActivity: 'Agentaktivitet',
    waitingForDecision: 'Venter på en beslutning',
    fromAgent: 'Fra {agent}, {when}',
    fromAgentOnly: 'Fra {agent}',
    unnamedAgent: 'En AI-agent',
    describe: '{agent} beder om at gøre dette for dig. Der sker ikke noget, før du godkender.',
    where: 'Hvor:',
    whereAsYou: '{where}, som dig.',
    connectionWrite: 'Forbindelsen er sat til Læsning og skrivning.',
    whyAsks: 'Hvorfor den spørger:',
    whyAskConnections:
      '{agent}s forbindelser er sat til Spørg hver gang, og denne handling er endnu ikke på listen over tilladte.',
    whyAsk:
      '{agent} er her sat til Spørg hver gang, og denne handling er endnu ikke på listen over tilladte.',
    whyFloor: 'Den slags handling spørger dig altid, uanset hvad {agent} er sat til.',
    whyEmail: 'Afsendelse fra Gmail og Outlook spørger dig altid, uanset hvad {agent} er sat til.',
    whyUnknown: 'Poneva kan ikke se, hvad dette ændrer, så den spørger dig først.',
    seeWhatItMayDo: 'Se, hvad den må',
    othersLikeThis: 'Andre som denne:',
    allowAgain: 'Spørg ikke igen om {action}',
    allowAgainDetail:
      '{agent} kan så gøre dette uden at spørge. Du kan fortryde det under Forbindelser.',
    approve: 'Godkend',
    decline: 'Afvis',
    approved: 'Godkendt. {agent} kan fortsætte.',
    declined: 'Afvist. Intet blev kørt.',
    requestVoid: 'Denne anmodning venter ikke længere, så intet blev kørt.',
    allowAgainUnavailable:
      'Poneva kan ikke huske dette valg for denne handling. Godkend igen for kun at tillade den denne ene gang.',
    decisionFailed: 'Vi kunne ikke sende din beslutning. Prøv igen.',
    loadFailed: 'Vi kunne ikke indlæse denne anmodning. Prøv igen om lidt.',
    loading: 'Indlæser…',
    alreadyDecided: 'Der er allerede truffet en beslutning om denne anmodning.',
    familyLibraryRead: 'Læs dit arbejde i Poneva',
    familyLibraryWrite: 'Gem arbejde og spørg Poneva',
    familyAppsAuthor: 'Opret og ændr apps',
    familyDataConnections: 'Brug dine forbindelser',
    familyOther: 'Handlinger i Poneva',
    allowedWithoutAsking: 'Tilladt uden at spørge',
    ranAutomatically: 'Kørte automatisk',
    refusedRead: '{name} er sat til Læsning, så intet blev ændret',
    refusedNothing: 'Intet blev ændret',
  },
  nl: {
    kindAgentRequest: 'Verzoek van agent',
    kindAgentActivity: 'Activiteit van agent',
    waitingForDecision: 'Wacht op een beslissing',
    fromAgent: 'Van {agent}, {when}',
    fromAgentOnly: 'Van {agent}',
    unnamedAgent: 'Een AI-agent',
    describe: '{agent} vraagt dit namens jou te doen. Er gebeurt niets totdat je goedkeurt.',
    where: 'Waar:',
    whereAsYou: '{where}, als jij.',
    connectionWrite: 'De koppeling staat op Lezen en schrijven.',
    whyAsks: 'Waarom het vraagt:',
    whyAskConnections:
      'De koppelingen van {agent} staan op Elke keer vragen en deze actie staat nog niet op de lijst met toegestane acties.',
    whyAsk:
      '{agent} staat hier op Elke keer vragen en deze actie staat nog niet op de lijst met toegestane acties.',
    whyFloor: 'Dit soort actie vraagt het je altijd, wat {agent} ook is ingesteld.',
    whyEmail: 'Versturen via Gmail en Outlook vraagt het je altijd, wat {agent} ook is ingesteld.',
    whyUnknown: 'Poneva kan niet zien wat dit wijzigt en vraagt het daarom eerst aan jou.',
    seeWhatItMayDo: 'Bekijk wat het mag doen',
    othersLikeThis: 'Meer zoals dit:',
    allowAgain: 'Niet meer vragen voor {action}',
    allowAgainDetail:
      '{agent} kan dit dan doen zonder te vragen. Je kunt dit ongedaan maken bij Koppelingen.',
    approve: 'Goedkeuren',
    decline: 'Weigeren',
    approved: 'Goedgekeurd. {agent} kan doorgaan.',
    declined: 'Geweigerd. Er is niets uitgevoerd.',
    requestVoid: 'Dit verzoek wacht niet meer, dus er is niets uitgevoerd.',
    allowAgainUnavailable:
      'Poneva kan deze keuze voor deze actie niet onthouden. Keur opnieuw goed om haar alleen deze ene keer toe te staan.',
    decisionFailed: 'We konden je beslissing niet versturen. Probeer het opnieuw.',
    loadFailed: 'We konden dit verzoek niet laden. Probeer het zo meteen opnieuw.',
    loading: 'Laden…',
    alreadyDecided: 'Over dit verzoek is al beslist.',
    familyLibraryRead: 'Je werk in Poneva lezen',
    familyLibraryWrite: 'Werk opslaan en Poneva iets vragen',
    familyAppsAuthor: 'Apps bouwen en wijzigen',
    familyDataConnections: 'Je koppelingen gebruiken',
    familyOther: 'Acties in Poneva',
    allowedWithoutAsking: 'Toegestaan zonder te vragen',
    ranAutomatically: 'Automatisch uitgevoerd',
    refusedRead: '{name} staat op Lezen, dus er is niets gewijzigd',
    refusedNothing: 'Er is niets gewijzigd',
  },
  en,
  et: {
    kindAgentRequest: 'Agendi taotlus',
    kindAgentActivity: 'Agendi tegevus',
    waitingForDecision: 'Ootab otsust',
    fromAgent: 'Saatja {agent}, {when}',
    fromAgentOnly: 'Saatja {agent}',
    unnamedAgent: 'Tehisintellekti agent',
    describe: '{agent} palub seda sinu eest teha. Midagi ei juhtu enne, kui kinnitad.',
    where: 'Kus:',
    whereAsYou: '{where}, sinuna.',
    connectionWrite: 'Ühendus on seatud režiimile Lugemine ja kirjutamine.',
    whyAsks: 'Miks küsib:',
    whyAskConnections:
      'Agendi {agent} ühendused on seatud režiimile Küsi iga kord ja see tegevus pole veel lubatute loendis.',
    whyAsk:
      'Agent {agent} on siin seatud režiimile Küsi iga kord ja see tegevus pole veel lubatute loendis.',
    whyFloor: 'Seda liiki tegevus küsib sinult alati, olenemata agendi {agent} seadest.',
    whyEmail:
      'Gmaili ja Outlooki saatmised küsivad sinult alati, olenemata agendi {agent} seadest.',
    whyUnknown: 'Poneva ei oska öelda, mida see muudab, seega küsib esmalt sinult.',
    seeWhatItMayDo: 'Vaata, mida ta tohib teha',
    othersLikeThis: 'Muud sarnased:',
    allowAgain: 'Ära küsi enam: {action}',
    allowAgainDetail:
      '{agent} saab seda siis teha küsimata. Saad selle tagasi võtta jaotises Ühendused.',
    approve: 'Kinnita',
    decline: 'Keeldu',
    approved: 'Kinnitatud. {agent} võib jätkata.',
    declined: 'Keeldutud. Midagi ei käivitatud.',
    requestVoid: 'See taotlus ei ootel enam, seega midagi ei käivitatud.',
    allowAgainUnavailable:
      'Poneva ei saa selle tegevuse puhul seda valikut meelde jätta. Kinnita uuesti, et lubada see ainult seekord.',
    decisionFailed: 'Otsuse saatmine ei õnnestunud. Proovi uuesti.',
    loadFailed: 'Taotluse laadimine ei õnnestunud. Proovi hetke pärast uuesti.',
    loading: 'Laadimine…',
    alreadyDecided: 'Selle taotluse kohta on otsus juba tehtud.',
    familyLibraryRead: 'Loe oma tööd Ponevas',
    familyLibraryWrite: 'Salvesta tööd ja küsi Ponevalt',
    familyAppsAuthor: 'Loo ja muuda rakendusi',
    familyDataConnections: 'Kasuta oma ühendusi',
    familyOther: 'Poneva tegevused',
    allowedWithoutAsking: 'Lubatud küsimata',
    ranAutomatically: 'Käivitus automaatselt',
    refusedRead: '{name} on seatud režiimile Lugemine, seega midagi ei muutunud',
    refusedNothing: 'Midagi ei muutunud',
  },
  fi: {
    kindAgentRequest: 'Agentin pyyntö',
    kindAgentActivity: 'Agentin toiminta',
    waitingForDecision: 'Odottaa päätöstäsi',
    fromAgent: 'Lähettäjä {agent}, {when}',
    fromAgentOnly: 'Lähettäjä {agent}',
    unnamedAgent: 'Tekoälyagentti',
    describe: '{agent} pyytää tekemään tämän puolestasi. Mitään ei tapahdu, ennen kuin hyväksyt.',
    where: 'Missä:',
    whereAsYou: '{where}, sinuna.',
    connectionWrite: 'Yhteys on asetettu tilaan Lukeminen ja kirjoittaminen.',
    whyAsks: 'Miksi se kysyy:',
    whyAskConnections:
      'Agentin {agent} yhteydet on asetettu tilaan Kysy joka kerta, eikä tämä toiminto ole vielä sallittujen luettelossa.',
    whyAsk:
      'Agentti {agent} on tässä asetettu tilaan Kysy joka kerta, eikä tämä toiminto ole vielä sallittujen luettelossa.',
    whyFloor: 'Tällainen toiminto kysyy aina sinulta, riippumatta agentin {agent} asetuksesta.',
    whyEmail:
      'Gmail- ja Outlook-lähetykset kysyvät aina sinulta, riippumatta agentin {agent} asetuksesta.',
    whyUnknown: 'Poneva ei voi tietää, mitä tämä muuttaa, joten se kysyy ensin sinulta.',
    seeWhatItMayDo: 'Katso, mitä se saa tehdä',
    othersLikeThis: 'Muita samanlaisia:',
    allowAgain: 'Älä kysy uudelleen: {action}',
    allowAgainDetail:
      '{agent} voi sitten tehdä tämän kysymättä. Voit perua tämän kohdassa Yhteydet.',
    approve: 'Hyväksy',
    decline: 'Hylkää',
    approved: 'Hyväksytty. {agent} voi jatkaa.',
    declined: 'Hylätty. Mitään ei suoritettu.',
    requestVoid: 'Tämä pyyntö ei enää odota, joten mitään ei suoritettu.',
    allowAgainUnavailable:
      'Poneva ei voi muistaa tätä valintaa tälle toiminnolle. Hyväksy uudelleen salliaksesi sen vain tämän kerran.',
    decisionFailed: 'Päätöstäsi ei voitu lähettää. Yritä uudelleen.',
    loadFailed: 'Pyyntöä ei voitu ladata. Yritä hetken kuluttua uudelleen.',
    loading: 'Ladataan…',
    alreadyDecided: 'Tästä pyynnöstä on jo päätetty.',
    familyLibraryRead: 'Lue työsi Ponevassa',
    familyLibraryWrite: 'Tallenna työtä ja kysy Ponevalta',
    familyAppsAuthor: 'Rakenna ja muuta sovelluksia',
    familyDataConnections: 'Käytä yhteyksiäsi',
    familyOther: 'Ponevan toiminnot',
    allowedWithoutAsking: 'Sallittu kysymättä',
    ranAutomatically: 'Suoritettiin automaattisesti',
    refusedRead: '{name} on asetettu tilaan Lukeminen, joten mikään ei muuttunut',
    refusedNothing: 'Mikään ei muuttunut',
  },
  fr: {
    kindAgentRequest: 'Demande d’un agent',
    kindAgentActivity: 'Activité d’un agent',
    waitingForDecision: 'En attente d’une décision',
    fromAgent: 'De {agent}, {when}',
    fromAgentOnly: 'De {agent}',
    unnamedAgent: 'Un agent IA',
    describe:
      '{agent} demande à faire cela pour vous. Rien ne se passe tant que vous n’approuvez pas.',
    where: 'Où :',
    whereAsYou: '{where}, en tant que vous.',
    connectionWrite: 'La connexion est réglée sur Lecture et écriture.',
    whyAsks: 'Pourquoi il demande :',
    whyAskConnections:
      'Les connexions de {agent} sont réglées sur Demander à chaque fois, et cette action n’est pas encore dans sa liste d’actions autorisées.',
    whyAsk:
      '{agent} est réglé ici sur Demander à chaque fois, et cette action n’est pas encore dans sa liste d’actions autorisées.',
    whyFloor: 'Ce type d’action vous demande toujours, quel que soit le réglage de {agent}.',
    whyEmail:
      'Les envois depuis Gmail et Outlook vous sont toujours demandés, quel que soit le réglage de {agent}.',
    whyUnknown: 'Poneva ne peut pas savoir ce que cela modifie, il vous demande donc d’abord.',
    seeWhatItMayDo: 'Voir ce qu’il peut faire',
    othersLikeThis: 'D’autres comme celle-ci :',
    allowAgain: 'Ne plus demander pour {action}',
    allowAgainDetail:
      '{agent} pourra alors le faire sans demander. Vous pouvez annuler cela dans Connexions.',
    approve: 'Approuver',
    decline: 'Refuser',
    approved: 'Approuvé. {agent} peut continuer.',
    declined: 'Refusé. Rien n’a été exécuté.',
    requestVoid: 'Cette demande n’est plus en attente, rien n’a donc été exécuté.',
    allowAgainUnavailable:
      'Poneva ne peut pas mémoriser ce choix pour cette action. Approuvez de nouveau pour l’autoriser cette fois seulement.',
    decisionFailed: 'Nous n’avons pas pu envoyer votre décision. Réessayez.',
    loadFailed: 'Nous n’avons pas pu charger cette demande. Réessayez dans un instant.',
    loading: 'Chargement…',
    alreadyDecided: 'Une décision a déjà été prise pour cette demande.',
    familyLibraryRead: 'Lire votre travail dans Poneva',
    familyLibraryWrite: 'Enregistrer du travail et interroger Poneva',
    familyAppsAuthor: 'Créer et modifier des apps',
    familyDataConnections: 'Utiliser vos connexions',
    familyOther: 'Actions Poneva',
    allowedWithoutAsking: 'Autorisé sans demander',
    ranAutomatically: 'Exécuté automatiquement',
    refusedRead: '{name} est réglée sur Lecture, rien n’a donc changé',
    refusedNothing: 'Rien n’a changé',
  },
  de: {
    kindAgentRequest: 'Anfrage eines Agenten',
    kindAgentActivity: 'Aktivität eines Agenten',
    waitingForDecision: 'Wartet auf eine Entscheidung',
    fromAgent: 'Von {agent}, {when}',
    fromAgentOnly: 'Von {agent}',
    unnamedAgent: 'Ein KI-Agent',
    describe: '{agent} bittet darum, dies für Sie zu tun. Nichts passiert, bis Sie zustimmen.',
    where: 'Wo:',
    whereAsYou: '{where}, als Sie.',
    connectionWrite: 'Die Verbindung ist auf Lesen und Schreiben gestellt.',
    whyAsks: 'Warum gefragt wird:',
    whyAskConnections:
      'Die Verbindungen von {agent} sind auf Jedes Mal fragen gestellt, und diese Aktion steht noch nicht auf der Liste der erlaubten.',
    whyAsk:
      '{agent} ist hier auf Jedes Mal fragen gestellt, und diese Aktion steht noch nicht auf der Liste der erlaubten.',
    whyFloor: 'Diese Art von Aktion fragt Sie immer, egal wie {agent} eingestellt ist.',
    whyEmail:
      'Das Senden über Gmail und Outlook fragt Sie immer, egal wie {agent} eingestellt ist.',
    whyUnknown: 'Poneva kann nicht erkennen, was dies ändert, und fragt Sie deshalb zuerst.',
    seeWhatItMayDo: 'Ansehen, was erlaubt ist',
    othersLikeThis: 'Weitere wie diese:',
    allowAgain: 'Für {action} nicht mehr fragen',
    allowAgainDetail:
      '{agent} kann das dann ohne Nachfrage tun. Sie können das unter Verbindungen rückgängig machen.',
    approve: 'Genehmigen',
    decline: 'Ablehnen',
    approved: 'Genehmigt. {agent} kann weitermachen.',
    declined: 'Abgelehnt. Nichts wurde ausgeführt.',
    requestVoid: 'Diese Anfrage wartet nicht mehr, daher wurde nichts ausgeführt.',
    allowAgainUnavailable:
      'Poneva kann sich diese Auswahl für diese Aktion nicht merken. Genehmigen Sie sie noch einmal, um sie nur dieses eine Mal zu erlauben.',
    decisionFailed: 'Ihre Entscheidung konnte nicht gesendet werden. Versuchen Sie es erneut.',
    loadFailed: 'Diese Anfrage konnte nicht geladen werden. Versuchen Sie es gleich noch einmal.',
    loading: 'Wird geladen…',
    alreadyDecided: 'Über diese Anfrage wurde bereits entschieden.',
    familyLibraryRead: 'Ihre Arbeit in Poneva lesen',
    familyLibraryWrite: 'Arbeit speichern und Poneva fragen',
    familyAppsAuthor: 'Apps erstellen und ändern',
    familyDataConnections: 'Ihre Verbindungen nutzen',
    familyOther: 'Aktionen in Poneva',
    allowedWithoutAsking: 'Ohne Nachfrage erlaubt',
    ranAutomatically: 'Automatisch ausgeführt',
    refusedRead: '{name} ist auf Lesen gestellt, daher wurde nichts geändert',
    refusedNothing: 'Nichts wurde geändert',
  },
  el: {
    kindAgentRequest: 'Αίτημα πράκτορα',
    kindAgentActivity: 'Δραστηριότητα πράκτορα',
    waitingForDecision: 'Σε αναμονή απόφασης',
    fromAgent: 'Από {agent}, {when}',
    fromAgentOnly: 'Από {agent}',
    unnamedAgent: 'Πράκτορας AI',
    describe: 'Ο/Η {agent} ζητά να το κάνει αυτό για εσάς. Δεν γίνεται τίποτα μέχρι να εγκρίνετε.',
    where: 'Πού:',
    whereAsYou: '{where}, ως εσείς.',
    connectionWrite: 'Η σύνδεση έχει οριστεί σε Ανάγνωση και εγγραφή.',
    whyAsks: 'Γιατί ρωτά:',
    whyAskConnections:
      'Οι συνδέσεις του/της {agent} έχουν οριστεί σε Ερώτηση κάθε φορά και αυτή η ενέργεια δεν βρίσκεται ακόμη στη λίστα των επιτρεπόμενων.',
    whyAsk:
      'Ο/Η {agent} έχει οριστεί εδώ σε Ερώτηση κάθε φορά και αυτή η ενέργεια δεν βρίσκεται ακόμη στη λίστα των επιτρεπόμενων.',
    whyFloor: 'Αυτό το είδος ενέργειας ρωτά πάντα εσάς, ανεξάρτητα από τη ρύθμιση του/της {agent}.',
    whyEmail:
      'Οι αποστολές από Gmail και Outlook ρωτούν πάντα εσάς, ανεξάρτητα από τη ρύθμιση του/της {agent}.',
    whyUnknown: 'Η Poneva δεν μπορεί να καταλάβει τι αλλάζει αυτό, γι’ αυτό ρωτά πρώτα εσάς.',
    seeWhatItMayDo: 'Δείτε τι επιτρέπεται να κάνει',
    othersLikeThis: 'Άλλα παρόμοια:',
    allowAgain: 'Να μη ρωτήσει ξανά για {action}',
    allowAgainDetail:
      'Ο/Η {agent} θα μπορεί τότε να το κάνει χωρίς να ρωτά. Μπορείτε να το αναιρέσετε στις Συνδέσεις.',
    approve: 'Έγκριση',
    decline: 'Απόρριψη',
    approved: 'Εγκρίθηκε. Ο/Η {agent} μπορεί να συνεχίσει.',
    declined: 'Απορρίφθηκε. Δεν εκτελέστηκε τίποτα.',
    requestVoid: 'Αυτό το αίτημα δεν περιμένει πια, γι’ αυτό δεν εκτελέστηκε τίποτα.',
    allowAgainUnavailable:
      'Η Poneva δεν μπορεί να θυμάται αυτή την επιλογή για αυτή την ενέργεια. Εγκρίνετε ξανά για να την επιτρέψετε μόνο αυτή τη φορά.',
    decisionFailed: 'Δεν ήταν δυνατή η αποστολή της απόφασής σας. Δοκιμάστε ξανά.',
    loadFailed: 'Δεν ήταν δυνατή η φόρτωση αυτού του αιτήματος. Δοκιμάστε ξανά σε λίγο.',
    loading: 'Φόρτωση…',
    alreadyDecided: 'Για αυτό το αίτημα έχει ήδη ληφθεί απόφαση.',
    familyLibraryRead: 'Ανάγνωση της δουλειάς σας στην Poneva',
    familyLibraryWrite: 'Αποθήκευση δουλειάς και ερωτήσεις στην Poneva',
    familyAppsAuthor: 'Δημιουργία και αλλαγή εφαρμογών',
    familyDataConnections: 'Χρήση των συνδέσεών σας',
    familyOther: 'Ενέργειες στην Poneva',
    allowedWithoutAsking: 'Επιτρέπεται χωρίς ερώτηση',
    ranAutomatically: 'Εκτελέστηκε αυτόματα',
    refusedRead: 'Η σύνδεση {name} έχει οριστεί σε Ανάγνωση, γι’ αυτό δεν άλλαξε τίποτα',
    refusedNothing: 'Δεν άλλαξε τίποτα',
  },
  hu: {
    kindAgentRequest: 'Ügynökkérés',
    kindAgentActivity: 'Ügynöktevékenység',
    waitingForDecision: 'Döntésre vár',
    fromAgent: 'Feladó: {agent}, {when}',
    fromAgentOnly: 'Feladó: {agent}',
    unnamedAgent: 'MI-ügynök',
    describe:
      '{agent} azt kéri, hogy ezt megtehesse Ön helyett. Addig nem történik semmi, amíg Ön jóvá nem hagyja.',
    where: 'Hol:',
    whereAsYou: '{where}, az Ön nevében.',
    connectionWrite: 'A kapcsolat Olvasás és írás módra van állítva.',
    whyAsks: 'Miért kérdez:',
    whyAskConnections:
      '{agent} kapcsolatai Kérdezzen minden alkalommal módra vannak állítva, és ez a művelet még nincs az engedélyezettek listáján.',
    whyAsk:
      '{agent} itt Kérdezzen minden alkalommal módra van állítva, és ez a művelet még nincs az engedélyezettek listáján.',
    whyFloor: 'Ez a fajta művelet mindig Önt kérdezi, akármire is van állítva {agent}.',
    whyEmail: 'A Gmail- és Outlook-küldések mindig Önt kérdezik, akármire is van állítva {agent}.',
    whyUnknown: 'A Poneva nem tudja megmondani, mit módosít ez, ezért először Önt kérdezi.',
    seeWhatItMayDo: 'Nézze meg, mit tehet',
    othersLikeThis: 'További hasonlók:',
    allowAgain: 'Ne kérdezzen többé: {action}',
    allowAgainDetail:
      '{agent} ezután kérdés nélkül megteheti ezt. Ezt a Kapcsolatok oldalon visszavonhatja.',
    approve: 'Jóváhagyás',
    decline: 'Elutasítás',
    approved: 'Jóváhagyva. {agent} folytathatja.',
    declined: 'Elutasítva. Semmi sem futott le.',
    requestVoid: 'Ez a kérés már nem vár, ezért semmi sem futott le.',
    allowAgainUnavailable:
      'A Poneva nem tudja megjegyezni ezt a választást ehhez a művelethez. Hagyja jóvá újra, hogy csak ezúttal engedélyezze.',
    decisionFailed: 'Nem tudtuk elküldeni a döntését. Próbálja újra.',
    loadFailed: 'Nem tudtuk betölteni ezt a kérést. Próbálja újra egy pillanat múlva.',
    loading: 'Betöltés…',
    alreadyDecided: 'Erről a kérésről már döntöttek.',
    familyLibraryRead: 'A Ponevában lévő munkájának olvasása',
    familyLibraryWrite: 'Munka mentése és kérdezés a Ponevától',
    familyAppsAuthor: 'Alkalmazások készítése és módosítása',
    familyDataConnections: 'Az Ön kapcsolatainak használata',
    familyOther: 'Poneva-műveletek',
    allowedWithoutAsking: 'Kérdés nélkül engedélyezve',
    ranAutomatically: 'Automatikusan lefutott',
    refusedRead: '{name} Olvasás módra van állítva, ezért semmi sem változott',
    refusedNothing: 'Semmi sem változott',
  },
  ga: {
    kindAgentRequest: 'Iarratas ó ghníomhaire',
    kindAgentActivity: 'Gníomhaíocht gníomhaire',
    waitingForDecision: 'Ag fanacht le cinneadh',
    fromAgent: 'Ó {agent}, {when}',
    fromAgentOnly: 'Ó {agent}',
    unnamedAgent: 'Gníomhaire AI',
    describe:
      'Iarrann {agent} é seo a dhéanamh ar do shon. Ní tharlaíonn aon rud go dtí go gceadaíonn tú é.',
    where: 'Cén áit:',
    whereAsYou: '{where}, mar tusa.',
    connectionWrite: 'Tá an nasc socraithe go Léamh agus scríobh.',
    whyAsks: 'Cén fáth a n-iarrann sé:',
    whyAskConnections:
      'Tá naisc {agent} socraithe go Fiafraigh gach uair, agus níl an gníomh seo ar liosta na gcinn ceadaithe go fóill.',
    whyAsk:
      'Tá {agent} socraithe anseo go Fiafraigh gach uair, agus níl an gníomh seo ar liosta na gcinn ceadaithe go fóill.',
    whyFloor: 'Fiafraíonn an cineál gníomh seo díot i gcónaí, is cuma cén socrú atá ag {agent}.',
    whyEmail:
      'Fiafraíonn seoltaí Gmail agus Outlook díot i gcónaí, is cuma cén socrú atá ag {agent}.',
    whyUnknown:
      'Ní féidir le Poneva a rá cad a athraíonn sé seo, mar sin fiafraíonn sé díotsa ar dtús.',
    seeWhatItMayDo: 'Féach cad a cheadaítear dó',
    othersLikeThis: 'Cinn eile mar seo:',
    allowAgain: 'Ná fiafraigh arís faoi {action}',
    allowAgainDetail:
      'Is féidir le {agent} é seo a dhéanamh ansin gan fiafraí. Is féidir leat é seo a chealú in Naisc.',
    approve: 'Ceadaigh',
    decline: 'Diúltaigh',
    approved: 'Ceadaithe. Is féidir le {agent} leanúint ar aghaidh.',
    declined: 'Diúltaithe. Níor rith aon rud.',
    requestVoid: 'Níl an t-iarratas seo ag fanacht a thuilleadh, mar sin níor rith aon rud.',
    allowAgainUnavailable:
      'Ní féidir le Poneva an rogha seo a mheabhrú don ghníomh seo. Ceadaigh arís é, agus ní cheadófar é ach an uair seo.',
    decisionFailed: 'Níorbh fhéidir do chinneadh a sheoladh. Bain triail eile as.',
    loadFailed: 'Níorbh fhéidir an t-iarratas seo a luchtú. Bain triail eile as i gceann tamaill.',
    loading: 'Ag luchtú…',
    alreadyDecided: 'Tá cinneadh déanta cheana faoin iarratas seo.',
    familyLibraryRead: 'Léigh do chuid oibre in Poneva',
    familyLibraryWrite: 'Sábháil obair agus fiafraigh de Poneva',
    familyAppsAuthor: 'Tóg agus athraigh Aipeanna',
    familyDataConnections: 'Úsáid do naisc',
    familyOther: 'Gníomhartha Poneva',
    allowedWithoutAsking: 'Ceadaithe gan fiafraí',
    ranAutomatically: 'Rith go huathoibríoch',
    refusedRead: 'Tá {name} socraithe go Léamh, mar sin níor athraigh aon rud',
    refusedNothing: 'Níor athraigh aon rud',
  },
  it: {
    kindAgentRequest: 'Richiesta di un agente',
    kindAgentActivity: 'Attività di un agente',
    waitingForDecision: 'In attesa di una decisione',
    fromAgent: 'Da {agent}, {when}',
    fromAgentOnly: 'Da {agent}',
    unnamedAgent: 'Un agente IA',
    describe: '{agent} chiede di farlo al posto tuo. Non succede nulla finché non approvi.',
    where: 'Dove:',
    whereAsYou: '{where}, come te.',
    connectionWrite: 'La connessione è impostata su Lettura e scrittura.',
    whyAsks: 'Perché lo chiede:',
    whyAskConnections:
      'Le connessioni di {agent} sono impostate su Chiedi ogni volta e questa azione non è ancora nell’elenco di quelle consentite.',
    whyAsk:
      '{agent} qui è impostato su Chiedi ogni volta e questa azione non è ancora nell’elenco di quelle consentite.',
    whyFloor: 'Questo tipo di azione chiede sempre a te, qualunque sia l’impostazione di {agent}.',
    whyEmail:
      'Gli invii da Gmail e Outlook chiedono sempre a te, qualunque sia l’impostazione di {agent}.',
    whyUnknown: 'Poneva non riesce a capire cosa cambi, quindi lo chiede prima a te.',
    seeWhatItMayDo: 'Vedi cosa può fare',
    othersLikeThis: 'Altre come questa:',
    allowAgain: 'Non chiedere più per {action}',
    allowAgainDetail: '{agent} potrà poi farlo senza chiedere. Puoi annullare in Connessioni.',
    approve: 'Approva',
    decline: 'Rifiuta',
    approved: 'Approvato. {agent} può continuare.',
    declined: 'Rifiutato. Non è stato eseguito nulla.',
    requestVoid: 'Questa richiesta non è più in attesa, quindi non è stato eseguito nulla.',
    allowAgainUnavailable:
      'Poneva non può ricordare questa scelta per questa azione. Approva di nuovo per consentirla solo questa volta.',
    decisionFailed: 'Non siamo riusciti a inviare la tua decisione. Riprova.',
    loadFailed: 'Non siamo riusciti a caricare questa richiesta. Riprova tra un momento.',
    loading: 'Caricamento…',
    alreadyDecided: 'Su questa richiesta è già stata presa una decisione.',
    familyLibraryRead: 'Leggere il tuo lavoro in Poneva',
    familyLibraryWrite: 'Salvare lavoro e chiedere a Poneva',
    familyAppsAuthor: 'Creare e modificare app',
    familyDataConnections: 'Usare le tue connessioni',
    familyOther: 'Azioni di Poneva',
    allowedWithoutAsking: 'Consentito senza chiedere',
    ranAutomatically: 'Eseguito automaticamente',
    refusedRead: '{name} è impostata su Lettura, quindi non è cambiato nulla',
    refusedNothing: 'Non è cambiato nulla',
  },
  lv: {
    kindAgentRequest: 'Aģenta pieprasījums',
    kindAgentActivity: 'Aģenta darbība',
    waitingForDecision: 'Gaida lēmumu',
    fromAgent: 'No: {agent}, {when}',
    fromAgentOnly: 'No: {agent}',
    unnamedAgent: 'MI aģents',
    describe: '{agent} lūdz to izdarīt jūsu vietā. Nekas nenotiks, kamēr neapstiprināsiet.',
    where: 'Kur:',
    whereAsYou: '{where}, kā jūs.',
    connectionWrite: 'Savienojums ir iestatīts uz Lasīšana un rakstīšana.',
    whyAsks: 'Kāpēc tas jautā:',
    whyAskConnections:
      'Aģenta {agent} savienojumi ir iestatīti uz Jautāt katru reizi, un šī darbība vēl nav atļauto sarakstā.',
    whyAsk:
      'Aģents {agent} šeit ir iestatīts uz Jautāt katru reizi, un šī darbība vēl nav atļauto sarakstā.',
    whyFloor: 'Šāda veida darbība vienmēr jautā jums, lai kā būtu iestatīts {agent}.',
    whyEmail: 'Gmail un Outlook sūtījumi vienmēr jautā jums, lai kā būtu iestatīts {agent}.',
    whyUnknown: 'Poneva nevar noteikt, ko tas maina, tāpēc vispirms jautā jums.',
    seeWhatItMayDo: 'Skatīt, ko tas drīkst darīt',
    othersLikeThis: 'Citi līdzīgi:',
    allowAgain: 'Vairs nejautāt par: {action}',
    allowAgainDetail: 'Tad {agent} to varēs darīt, nejautājot. To var atsaukt sadaļā Savienojumi.',
    approve: 'Apstiprināt',
    decline: 'Noraidīt',
    approved: 'Apstiprināts. {agent} var turpināt.',
    declined: 'Noraidīts. Nekas netika izpildīts.',
    requestVoid: 'Šis pieprasījums vairs negaida, tāpēc nekas netika izpildīts.',
    allowAgainUnavailable:
      'Poneva nevar atcerēties šo izvēli šai darbībai. Apstipriniet vēlreiz, lai atļautu to tikai šoreiz.',
    decisionFailed: 'Neizdevās nosūtīt jūsu lēmumu. Mēģiniet vēlreiz.',
    loadFailed: 'Neizdevās ielādēt šo pieprasījumu. Pēc brīža mēģiniet vēlreiz.',
    loading: 'Ielādē…',
    alreadyDecided: 'Par šo pieprasījumu jau ir pieņemts lēmums.',
    familyLibraryRead: 'Lasīt jūsu darbu Poneva',
    familyLibraryWrite: 'Saglabāt darbu un jautāt Poneva',
    familyAppsAuthor: 'Veidot un mainīt lietotnes',
    familyDataConnections: 'Izmantot jūsu savienojumus',
    familyOther: 'Poneva darbības',
    allowedWithoutAsking: 'Atļauts bez jautāšanas',
    ranAutomatically: 'Izpildīts automātiski',
    refusedRead: '{name} ir iestatīts uz Lasīšana, tāpēc nekas nemainījās',
    refusedNothing: 'Nekas nemainījās',
  },
  lt: {
    kindAgentRequest: 'Agento užklausa',
    kindAgentActivity: 'Agento veikla',
    waitingForDecision: 'Laukia sprendimo',
    fromAgent: 'Nuo: {agent}, {when}',
    fromAgentOnly: 'Nuo: {agent}',
    unnamedAgent: 'DI agentas',
    describe: '{agent} prašo tai atlikti už jus. Nieko nevyksta, kol nepatvirtinsite.',
    where: 'Kur:',
    whereAsYou: '{where}, kaip jūs.',
    connectionWrite: 'Ryšys nustatytas į Skaitymas ir rašymas.',
    whyAsks: 'Kodėl klausia:',
    whyAskConnections:
      '{agent} ryšiai nustatyti į Klausti kaskart, o šio veiksmo dar nėra leidžiamųjų sąraše.',
    whyAsk: '{agent} čia nustatytas į Klausti kaskart, o šio veiksmo dar nėra leidžiamųjų sąraše.',
    whyFloor: 'Šio tipo veiksmas visada klausia jūsų, kad ir kaip būtų nustatytas {agent}.',
    whyEmail:
      'Gmail ir Outlook siuntimai visada klausia jūsų, kad ir kaip būtų nustatytas {agent}.',
    whyUnknown: 'Poneva negali nustatyti, ką tai keičia, todėl pirmiausia klausia jūsų.',
    seeWhatItMayDo: 'Žiūrėti, ką jis gali daryti',
    othersLikeThis: 'Kiti panašūs:',
    allowAgain: 'Daugiau nebeklausti dėl: {action}',
    allowAgainDetail:
      'Tada {agent} galės tai daryti neklausdamas. Tai galite atšaukti skiltyje Ryšiai.',
    approve: 'Patvirtinti',
    decline: 'Atmesti',
    approved: 'Patvirtinta. {agent} gali tęsti.',
    declined: 'Atmesta. Niekas nebuvo paleista.',
    requestVoid: 'Ši užklausa nebelaukia, todėl niekas nebuvo paleista.',
    allowAgainUnavailable:
      'Poneva negali įsiminti šio pasirinkimo šiam veiksmui. Patvirtinkite dar kartą, kad jį leistumėte tik šį kartą.',
    decisionFailed: 'Nepavyko išsiųsti jūsų sprendimo. Bandykite dar kartą.',
    loadFailed: 'Nepavyko įkelti šios užklausos. Po akimirkos bandykite dar kartą.',
    loading: 'Įkeliama…',
    alreadyDecided: 'Dėl šios užklausos jau nuspręsta.',
    familyLibraryRead: 'Skaityti jūsų darbą Poneva',
    familyLibraryWrite: 'Įrašyti darbą ir klausti Poneva',
    familyAppsAuthor: 'Kurti ir keisti programas',
    familyDataConnections: 'Naudoti jūsų ryšius',
    familyOther: 'Poneva veiksmai',
    allowedWithoutAsking: 'Leidžiama neklausiant',
    ranAutomatically: 'Paleista automatiškai',
    refusedRead: '{name} nustatytas į Skaitymas, todėl niekas nepasikeitė',
    refusedNothing: 'Niekas nepasikeitė',
  },
  mt: {
    kindAgentRequest: 'Talba minn aġent',
    kindAgentActivity: 'Attività ta’ aġent',
    waitingForDecision: 'Qed tistenna deċiżjoni',
    fromAgent: 'Minn {agent}, {when}',
    fromAgentOnly: 'Minn {agent}',
    unnamedAgent: 'Aġent tal-IA',
    describe: '{agent} qed jitlob li jagħmel dan f’ismek. Ma jiġri xejn sakemm ma tapprovax.',
    where: 'Fejn:',
    whereAsYou: '{where}, bħala int.',
    connectionWrite: 'Il-konnessjoni hija ssettjata fuq Qari u kitba.',
    whyAsks: 'Għaliex jistaqsi:',
    whyAskConnections:
      'Il-konnessjonijiet ta’ {agent} huma ssettjati fuq Staqsi kull darba, u din l-azzjoni għadha mhix fil-lista ta’ dawk permessi.',
    whyAsk:
      '{agent} hawnhekk huwa ssettjat fuq Staqsi kull darba, u din l-azzjoni għadha mhix fil-lista ta’ dawk permessi.',
    whyFloor: 'Dan it-tip ta’ azzjoni dejjem jistaqsik, kemm jekk {agent} huwa ssettjat kif inhu.',
    whyEmail:
      'L-ibgħat minn Gmail u Outlook dejjem jistaqsik, kemm jekk {agent} huwa ssettjat kif inhu.',
    whyUnknown: 'Poneva ma tistax tgħid x’jbiddel dan, għalhekk tistaqsik l-ewwel.',
    seeWhatItMayDo: 'Ara x’jista’ jagħmel',
    othersLikeThis: 'Oħrajn bħal dan:',
    allowAgain: 'Taqsamx staqsi għal {action}',
    allowAgainDetail:
      '{agent} jista’ mbagħad jagħmel dan mingħajr ma jistaqsi. Tista’ tneħħi dan f’Konnessjonijiet.',
    approve: 'Approva',
    decline: 'Irrifjuta',
    approved: 'Approvat. {agent} jista’ jkompli.',
    declined: 'Irrifjutat. Ma ġie esegwit xejn.',
    requestVoid: 'Din it-talba m’għadhiex tistenna, għalhekk ma ġie esegwit xejn.',
    allowAgainUnavailable:
      'Poneva ma tistax tiftakar din l-għażla għal din l-azzjoni. Approva mill-ġdid biex tippermettiha din id-darba biss.',
    decisionFailed: 'Ma stajniex nibagħtu d-deċiżjoni tiegħek. Erġa’ pprova.',
    loadFailed: 'Ma stajniex inepiskaw din it-talba. Erġa’ pprova wara ftit.',
    loading: 'Qed jitgħabba…',
    alreadyDecided: 'Diġà ttieħdet deċiżjoni dwar din it-talba.',
    familyLibraryRead: 'Aqra x-xogħol tiegħek f’Poneva',
    familyLibraryWrite: 'Issejvja x-xogħol u staqsi lil Poneva',
    familyAppsAuthor: 'Ibni u biddel l-Apps',
    familyDataConnections: 'Uża l-konnessjonijiet tiegħek',
    familyOther: 'Azzjonijiet ta’ Poneva',
    allowedWithoutAsking: 'Permess mingħajr ma jistaqsi',
    ranAutomatically: 'Esegwit awtomatikament',
    refusedRead: '{name} huwa ssettjat fuq Qari, għalhekk ma nbidel xejn',
    refusedNothing: 'Ma nbidel xejn',
  },
  pl: {
    kindAgentRequest: 'Prośba agenta',
    kindAgentActivity: 'Aktywność agenta',
    waitingForDecision: 'Czeka na decyzję',
    fromAgent: 'Od: {agent}, {when}',
    fromAgentOnly: 'Od: {agent}',
    unnamedAgent: 'Agent AI',
    describe:
      '{agent} prosi o wykonanie tego za Ciebie. Nic się nie stanie, dopóki nie zatwierdzisz.',
    where: 'Gdzie:',
    whereAsYou: '{where}, jako Ty.',
    connectionWrite: 'Połączenie jest ustawione na Odczyt i zapis.',
    whyAsks: 'Dlaczego pyta:',
    whyAskConnections:
      'Połączenia agenta {agent} są ustawione na Pytaj za każdym razem, a tej czynności nie ma jeszcze na liście dozwolonych.',
    whyAsk:
      'Agent {agent} jest tu ustawiony na Pytaj za każdym razem, a tej czynności nie ma jeszcze na liście dozwolonych.',
    whyFloor: 'Ten rodzaj czynności zawsze pyta Ciebie, niezależnie od ustawienia agenta {agent}.',
    whyEmail:
      'Wysyłanie z Gmaila i Outlooka zawsze pyta Ciebie, niezależnie od ustawienia agenta {agent}.',
    whyUnknown: 'Poneva nie potrafi ocenić, co to zmienia, więc najpierw pyta Ciebie.',
    seeWhatItMayDo: 'Zobacz, co może robić',
    othersLikeThis: 'Inne takie:',
    allowAgain: 'Nie pytaj ponownie o: {action}',
    allowAgainDetail:
      '{agent} będzie mógł to wtedy robić bez pytania. Możesz to cofnąć w Połączeniach.',
    approve: 'Zatwierdź',
    decline: 'Odrzuć',
    approved: 'Zatwierdzono. {agent} może kontynuować.',
    declined: 'Odrzucono. Nic nie zostało wykonane.',
    requestVoid: 'Ta prośba już nie czeka, więc nic nie zostało wykonane.',
    allowAgainUnavailable:
      'Poneva nie może zapamiętać tego wyboru dla tej czynności. Zatwierdź ponownie, aby zezwolić na nią tylko tym razem.',
    decisionFailed: 'Nie udało się wysłać Twojej decyzji. Spróbuj ponownie.',
    loadFailed: 'Nie udało się wczytać tej prośby. Spróbuj ponownie za chwilę.',
    loading: 'Wczytywanie…',
    alreadyDecided: 'Ta prośba została już rozstrzygnięta.',
    familyLibraryRead: 'Odczyt Twojej pracy w Poneva',
    familyLibraryWrite: 'Zapisywanie pracy i pytania do Poneva',
    familyAppsAuthor: 'Tworzenie i zmiana aplikacji',
    familyDataConnections: 'Korzystanie z Twoich połączeń',
    familyOther: 'Działania w Poneva',
    allowedWithoutAsking: 'Dozwolone bez pytania',
    ranAutomatically: 'Wykonano automatycznie',
    refusedRead: 'Połączenie {name} jest ustawione na Odczyt, więc nic się nie zmieniło',
    refusedNothing: 'Nic się nie zmieniło',
  },
  pt: {
    kindAgentRequest: 'Pedido de um agente',
    kindAgentActivity: 'Atividade de um agente',
    waitingForDecision: 'À espera de uma decisão',
    fromAgent: 'De {agent}, {when}',
    fromAgentOnly: 'De {agent}',
    unnamedAgent: 'Um agente de IA',
    describe: '{agent} pede para fazer isto por si. Nada acontece até aprovar.',
    where: 'Onde:',
    whereAsYou: '{where}, como você.',
    connectionWrite: 'A ligação está definida como Leitura e escrita.',
    whyAsks: 'Porque pergunta:',
    whyAskConnections:
      'As ligações de {agent} estão definidas como Perguntar sempre e esta ação ainda não está na lista de permitidas.',
    whyAsk:
      '{agent} está aqui definido como Perguntar sempre e esta ação ainda não está na lista de permitidas.',
    whyFloor: 'Este tipo de ação pergunta-lhe sempre, seja qual for a definição de {agent}.',
    whyEmail:
      'Os envios do Gmail e do Outlook perguntam-lhe sempre, seja qual for a definição de {agent}.',
    whyUnknown: 'A Poneva não consegue saber o que isto altera, por isso pergunta-lhe primeiro.',
    seeWhatItMayDo: 'Ver o que pode fazer',
    othersLikeThis: 'Outros como este:',
    allowAgain: 'Não perguntar de novo para {action}',
    allowAgainDetail: '{agent} poderá então fazê-lo sem perguntar. Pode anular isto em Ligações.',
    approve: 'Aprovar',
    decline: 'Recusar',
    approved: 'Aprovado. {agent} pode continuar.',
    declined: 'Recusado. Nada foi executado.',
    requestVoid: 'Este pedido já não está à espera, por isso nada foi executado.',
    allowAgainUnavailable:
      'A Poneva não consegue memorizar esta escolha para esta ação. Aprove novamente para a permitir apenas desta vez.',
    decisionFailed: 'Não foi possível enviar a sua decisão. Tente novamente.',
    loadFailed: 'Não foi possível carregar este pedido. Tente novamente dentro de instantes.',
    loading: 'A carregar…',
    alreadyDecided: 'Este pedido já foi decidido.',
    familyLibraryRead: 'Ler o seu trabalho na Poneva',
    familyLibraryWrite: 'Guardar trabalho e perguntar à Poneva',
    familyAppsAuthor: 'Criar e alterar apps',
    familyDataConnections: 'Usar as suas ligações',
    familyOther: 'Ações da Poneva',
    allowedWithoutAsking: 'Permitido sem perguntar',
    ranAutomatically: 'Executado automaticamente',
    refusedRead: '{name} está definida como Leitura, por isso nada mudou',
    refusedNothing: 'Nada mudou',
  },
  ro: {
    kindAgentRequest: 'Cerere de la un agent',
    kindAgentActivity: 'Activitatea unui agent',
    waitingForDecision: 'Așteaptă o decizie',
    fromAgent: 'De la {agent}, {when}',
    fromAgentOnly: 'De la {agent}',
    unnamedAgent: 'Un agent AI',
    describe: '{agent} cere să facă asta în locul tău. Nu se întâmplă nimic până nu aprobi.',
    where: 'Unde:',
    whereAsYou: '{where}, ca tine.',
    connectionWrite: 'Conexiunea este setată pe Citire și scriere.',
    whyAsks: 'De ce întreabă:',
    whyAskConnections:
      'Conexiunile lui {agent} sunt setate pe Întreabă de fiecare dată, iar această acțiune nu se află încă pe lista celor permise.',
    whyAsk:
      '{agent} este setat aici pe Întreabă de fiecare dată, iar această acțiune nu se află încă pe lista celor permise.',
    whyFloor: 'Acest tip de acțiune te întreabă întotdeauna, indiferent cum este setat {agent}.',
    whyEmail:
      'Trimiterile din Gmail și Outlook te întreabă întotdeauna, indiferent cum este setat {agent}.',
    whyUnknown: 'Poneva nu poate spune ce schimbă aceasta, așa că te întreabă mai întâi pe tine.',
    seeWhatItMayDo: 'Vezi ce are voie să facă',
    othersLikeThis: 'Altele ca aceasta:',
    allowAgain: 'Nu mai întreba pentru {action}',
    allowAgainDetail: 'Apoi {agent} poate face asta fără să întrebe. Poți anula în Conexiuni.',
    approve: 'Aprobă',
    decline: 'Refuză',
    approved: 'Aprobat. {agent} poate continua.',
    declined: 'Refuzat. Nu s-a executat nimic.',
    requestVoid: 'Această cerere nu mai așteaptă, așa că nu s-a executat nimic.',
    allowAgainUnavailable:
      'Poneva nu poate ține minte această alegere pentru această acțiune. Aprobă din nou pentru a o permite doar de data aceasta.',
    decisionFailed: 'Nu am putut trimite decizia ta. Încearcă din nou.',
    loadFailed: 'Nu am putut încărca această cerere. Încearcă din nou peste puțin timp.',
    loading: 'Se încarcă…',
    alreadyDecided: 'S-a decis deja asupra acestei cereri.',
    familyLibraryRead: 'Citirea lucrărilor tale din Poneva',
    familyLibraryWrite: 'Salvarea lucrărilor și întrebări către Poneva',
    familyAppsAuthor: 'Crearea și modificarea aplicațiilor',
    familyDataConnections: 'Folosirea conexiunilor tale',
    familyOther: 'Acțiuni Poneva',
    allowedWithoutAsking: 'Permis fără întrebare',
    ranAutomatically: 'Executat automat',
    refusedRead: '{name} este setată pe Citire, așa că nu s-a schimbat nimic',
    refusedNothing: 'Nu s-a schimbat nimic',
  },
  sk: {
    kindAgentRequest: 'Žiadosť agenta',
    kindAgentActivity: 'Aktivita agenta',
    waitingForDecision: 'Čaká na rozhodnutie',
    fromAgent: 'Od: {agent}, {when}',
    fromAgentOnly: 'Od: {agent}',
    unnamedAgent: 'AI agent',
    describe: '{agent} žiada o vykonanie tejto akcie za vás. Nič sa nestane, kým ju neschválite.',
    where: 'Kde:',
    whereAsYou: '{where}, ako vy.',
    connectionWrite: 'Pripojenie je nastavené na Čítanie a zápis.',
    whyAsks: 'Prečo sa pýta:',
    whyAskConnections:
      'Pripojenia agenta {agent} sú nastavené na Pýtať sa zakaždým a táto akcia zatiaľ nie je v zozname povolených.',
    whyAsk:
      'Agent {agent} je tu nastavený na Pýtať sa zakaždým a táto akcia zatiaľ nie je v zozname povolených.',
    whyFloor: 'Tento druh akcie sa vás pýta vždy, nech je {agent} nastavený akokoľvek.',
    whyEmail:
      'Odosielanie z Gmailu a Outlooku sa vás pýta vždy, nech je {agent} nastavený akokoľvek.',
    whyUnknown: 'Poneva nevie určiť, čo sa tým zmení, preto sa najprv opýta vás.',
    seeWhatItMayDo: 'Pozrite sa, čo smie robiť',
    othersLikeThis: 'Ďalšie podobné:',
    allowAgain: 'Už sa nepýtať na {action}',
    allowAgainDetail: '{agent} to potom môže robiť bez pýtania. Môžete to vrátiť v Pripojeniach.',
    approve: 'Schváliť',
    decline: 'Odmietnuť',
    approved: 'Schválené. {agent} môže pokračovať.',
    declined: 'Odmietnuté. Nič sa nevykonalo.',
    requestVoid: 'Táto žiadosť už nečaká, takže sa nič nevykonalo.',
    allowAgainUnavailable:
      'Poneva si túto voľbu pre túto akciu nemôže zapamätať. Schváľte ju znova, aby sa povolila len tentoraz.',
    decisionFailed: 'Vaše rozhodnutie sa nepodarilo odoslať. Skúste to znova.',
    loadFailed: 'Túto žiadosť sa nepodarilo načítať. Skúste to o chvíľu znova.',
    loading: 'Načítava sa…',
    alreadyDecided: 'O tejto žiadosti už bolo rozhodnuté.',
    familyLibraryRead: 'Čítanie vašej práce v Poneva',
    familyLibraryWrite: 'Ukladanie práce a otázky pre Ponevu',
    familyAppsAuthor: 'Vytváranie a úprava aplikácií',
    familyDataConnections: 'Používanie vašich pripojení',
    familyOther: 'Akcie v Poneva',
    allowedWithoutAsking: 'Povolené bez pýtania',
    ranAutomatically: 'Vykonané automaticky',
    refusedRead: 'Pripojenie {name} je nastavené na Čítanie, takže sa nič nezmenilo',
    refusedNothing: 'Nič sa nezmenilo',
  },
  sl: {
    kindAgentRequest: 'Zahteva agenta',
    kindAgentActivity: 'Dejavnost agenta',
    waitingForDecision: 'Čaka na odločitev',
    fromAgent: 'Od: {agent}, {when}',
    fromAgentOnly: 'Od: {agent}',
    unnamedAgent: 'Agent UI',
    describe: '{agent} prosi, da to stori namesto vas. Nič se ne zgodi, dokler ne odobrite.',
    where: 'Kje:',
    whereAsYou: '{where}, kot vi.',
    connectionWrite: 'Povezava je nastavljena na Branje in pisanje.',
    whyAsks: 'Zakaj sprašuje:',
    whyAskConnections:
      'Povezave agenta {agent} so nastavljene na Vprašaj vsakič, tega dejanja pa še ni na seznamu dovoljenih.',
    whyAsk:
      'Agent {agent} je tukaj nastavljen na Vprašaj vsakič, tega dejanja pa še ni na seznamu dovoljenih.',
    whyFloor: 'Ta vrsta dejanja vas vedno vpraša, ne glede na nastavitev agenta {agent}.',
    whyEmail:
      'Pošiljanje iz Gmaila in Outlooka vas vedno vpraša, ne glede na nastavitev agenta {agent}.',
    whyUnknown: 'Poneva ne more ugotoviti, kaj to spremeni, zato najprej vpraša vas.',
    seeWhatItMayDo: 'Poglejte, kaj sme početi',
    othersLikeThis: 'Druge podobne:',
    allowAgain: 'Ne vprašaj več za: {action}',
    allowAgainDetail:
      '{agent} bo to potem lahko počel brez vprašanja. To lahko razveljavite v Povezavah.',
    approve: 'Odobri',
    decline: 'Zavrni',
    approved: 'Odobreno. {agent} lahko nadaljuje.',
    declined: 'Zavrnjeno. Nič ni bilo izvedeno.',
    requestVoid: 'Ta zahteva ne čaka več, zato nič ni bilo izvedeno.',
    allowAgainUnavailable:
      'Poneva si te izbire za to dejanje ne more zapomniti. Odobrite znova, da ga dovolite samo tokrat.',
    decisionFailed: 'Vaše odločitve ni bilo mogoče poslati. Poskusite znova.',
    loadFailed: 'Te zahteve ni bilo mogoče naložiti. Čez trenutek poskusite znova.',
    loading: 'Nalaganje …',
    alreadyDecided: 'O tej zahtevi je bilo že odločeno.',
    familyLibraryRead: 'Branje vašega dela v Poneva',
    familyLibraryWrite: 'Shranjevanje dela in spraševanje Poneve',
    familyAppsAuthor: 'Ustvarjanje in spreminjanje aplikacij',
    familyDataConnections: 'Uporaba vaših povezav',
    familyOther: 'Dejanja v Poneva',
    allowedWithoutAsking: 'Dovoljeno brez vprašanja',
    ranAutomatically: 'Izvedeno samodejno',
    refusedRead: 'Povezava {name} je nastavljena na Branje, zato se ni nič spremenilo',
    refusedNothing: 'Nič se ni spremenilo',
  },
  es: {
    kindAgentRequest: 'Solicitud de un agente',
    kindAgentActivity: 'Actividad de un agente',
    waitingForDecision: 'Esperando una decisión',
    fromAgent: 'De {agent}, {when}',
    fromAgentOnly: 'De {agent}',
    unnamedAgent: 'Un agente de IA',
    describe: '{agent} pide hacer esto por ti. No ocurre nada hasta que lo apruebes.',
    where: 'Dónde:',
    whereAsYou: '{where}, como tú.',
    connectionWrite: 'La conexión está configurada en Lectura y escritura.',
    whyAsks: 'Por qué pregunta:',
    whyAskConnections:
      'Las conexiones de {agent} están configuradas en Preguntar cada vez y esta acción aún no está en su lista de permitidas.',
    whyAsk:
      '{agent} está configurado aquí en Preguntar cada vez y esta acción aún no está en su lista de permitidas.',
    whyFloor: 'Este tipo de acción siempre te pregunta, sea cual sea la configuración de {agent}.',
    whyEmail:
      'Los envíos de Gmail y Outlook siempre te lo preguntan, sea cual sea la configuración de {agent}.',
    whyUnknown: 'Poneva no puede saber qué cambia esto, así que te pregunta primero.',
    seeWhatItMayDo: 'Ver qué puede hacer',
    othersLikeThis: 'Otras como esta:',
    allowAgain: 'No volver a preguntar por {action}',
    allowAgainDetail: '{agent} podrá hacerlo sin preguntar. Puedes deshacerlo en Conexiones.',
    approve: 'Aprobar',
    decline: 'Rechazar',
    approved: 'Aprobado. {agent} puede continuar.',
    declined: 'Rechazado. No se ejecutó nada.',
    requestVoid: 'Esta solicitud ya no está en espera, así que no se ejecutó nada.',
    allowAgainUnavailable:
      'Poneva no puede recordar esta elección para esta acción. Aprueba de nuevo para permitirla solo esta vez.',
    decisionFailed: 'No pudimos enviar tu decisión. Inténtalo de nuevo.',
    loadFailed: 'No pudimos cargar esta solicitud. Inténtalo de nuevo en un momento.',
    loading: 'Cargando…',
    alreadyDecided: 'Ya se decidió sobre esta solicitud.',
    familyLibraryRead: 'Leer tu trabajo en Poneva',
    familyLibraryWrite: 'Guardar trabajo y preguntar a Poneva',
    familyAppsAuthor: 'Crear y cambiar apps',
    familyDataConnections: 'Usar tus conexiones',
    familyOther: 'Acciones de Poneva',
    allowedWithoutAsking: 'Permitido sin preguntar',
    ranAutomatically: 'Ejecutado automáticamente',
    refusedRead: '{name} está configurada en Lectura, así que no cambió nada',
    refusedNothing: 'No cambió nada',
  },
  sv: {
    kindAgentRequest: 'Begäran från agent',
    kindAgentActivity: 'Agentaktivitet',
    waitingForDecision: 'Väntar på ett beslut',
    fromAgent: 'Från {agent}, {when}',
    fromAgentOnly: 'Från {agent}',
    unnamedAgent: 'En AI-agent',
    describe: '{agent} ber att få göra detta åt dig. Inget händer förrän du godkänner.',
    where: 'Var:',
    whereAsYou: '{where}, som du.',
    connectionWrite: 'Anslutningen är inställd på Läsning och skrivning.',
    whyAsks: 'Därför frågar den:',
    whyAskConnections:
      '{agent}s anslutningar är inställda på Fråga varje gång, och den här åtgärden finns inte på listan över tillåtna ännu.',
    whyAsk:
      '{agent} är här inställd på Fråga varje gång, och den här åtgärden finns inte på listan över tillåtna ännu.',
    whyFloor: 'Den här sortens åtgärd frågar dig alltid, oavsett hur {agent} är inställd.',
    whyEmail: 'Utskick från Gmail och Outlook frågar dig alltid, oavsett hur {agent} är inställd.',
    whyUnknown: 'Poneva kan inte avgöra vad detta ändrar och frågar därför dig först.',
    seeWhatItMayDo: 'Se vad den får göra',
    othersLikeThis: 'Fler som den här:',
    allowAgain: 'Fråga inte igen om {action}',
    allowAgainDetail:
      '{agent} kan då göra detta utan att fråga. Du kan ångra det under Anslutningar.',
    approve: 'Godkänn',
    decline: 'Avböj',
    approved: 'Godkänt. {agent} kan fortsätta.',
    declined: 'Avböjt. Inget kördes.',
    requestVoid: 'Den här begäran väntar inte längre, så inget kördes.',
    allowAgainUnavailable:
      'Poneva kan inte komma ihåg det här valet för den här åtgärden. Godkänn igen för att tillåta den bara den här gången.',
    decisionFailed: 'Vi kunde inte skicka ditt beslut. Försök igen.',
    loadFailed: 'Vi kunde inte läsa in begäran. Försök igen om en stund.',
    loading: 'Läser in…',
    alreadyDecided: 'Det har redan fattats ett beslut om den här begäran.',
    familyLibraryRead: 'Läsa ditt arbete i Poneva',
    familyLibraryWrite: 'Spara arbete och fråga Poneva',
    familyAppsAuthor: 'Bygga och ändra appar',
    familyDataConnections: 'Använda dina anslutningar',
    familyOther: 'Åtgärder i Poneva',
    allowedWithoutAsking: 'Tillåtet utan att fråga',
    ranAutomatically: 'Kördes automatiskt',
    refusedRead: '{name} är inställd på Läsning, så inget ändrades',
    refusedNothing: 'Inget ändrades',
  },
};

export default agentRequestsCopy;
